import React, { useRef, useState } from "react";
import { IoImageOutline, IoChatbubbleEllipsesOutline } from "react-icons/io5";

function ProductImage({ src, alt, darkMode }) {
    const [failed, setFailed] = useState(false);

    if (!src || failed) {
        return (
            <div
                className={`tw-h-32 tw-w-full tw-flex tw-items-center tw-justify-center ${
                    darkMode
                        ? "tw-bg-gray-800/80 tw-text-gray-600"
                        : "tw-bg-gray-100 tw-text-gray-300"
                }`}
            >
                <IoImageOutline size={28} />
            </div>
        );
    }

    return (
        <div
            className={`tw-h-32 tw-w-full tw-overflow-hidden ${
                darkMode ? "tw-bg-gray-800" : "tw-bg-gray-50"
            }`}
        >
            <img
                src={src}
                alt={alt}
                className="tw-w-full tw-h-full tw-object-cover tw-transition-transform hover:tw-scale-105"
                draggable={false}
                onError={() => setFailed(true)}
            />
        </div>
    );
}

/**
 * Adds commas to a price string or number.
 * Handles strings like "₦210000.00" or "$25000" and numbers like 25000.
 */
function formatPrice(value) {
    if (value == null) return "";
    const str = String(value);
    // Split into currency prefix, number, and suffix
    const match = str.match(/^([^\d]*)([\d]+(?:\.[\d]*)?)(.*)$/);
    if (!match) return str;
    const [, prefix, num, suffix] = match;
    const [whole, decimal] = num.split(".");
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return prefix + withCommas + (decimal !== undefined ? "." + decimal : "") + suffix;
}

export function ProductCardList({ products, darkMode, onMentionProduct }) {
    if (!products || !Array.isArray(products) || products.length === 0) {
        return null;
    }

    const scrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragState = useRef({ startX: 0, scrollLeft: 0, moved: false });

    const onPointerDown = (e) => {
        const el = scrollRef.current;
        if (!el) return;
        setIsDragging(true);
        dragState.current.startX = e.clientX;
        dragState.current.scrollLeft = el.scrollLeft;
        dragState.current.moved = false;
        el.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e) => {
        if (!isDragging) return;
        const el = scrollRef.current;
        if (!el) return;
        const dx = e.clientX - dragState.current.startX;
        if (Math.abs(dx) > 3) dragState.current.moved = true;
        el.scrollLeft = dragState.current.scrollLeft - dx;
    };

    const onPointerUp = (e) => {
        setIsDragging(false);
        scrollRef.current?.releasePointerCapture(e.pointerId);
    };

    const handleProductClick = (product) => {
        if (dragState.current.moved) return;
        const id = product._id || product.id;
        if (!id) return;
        const path = `/products/${id}`;
        // Dispatch event so the host app's router can handle navigation
        const event = new CustomEvent("salesive:navigate", {
            detail: { path, productId: id },
            bubbles: true,
            cancelable: true,
        });
        const handled = !window.dispatchEvent(event);
        // If host app didn't preventDefault, fall back to direct navigation
        if (!handled) {
            window.location.href = path;
        }
    };

    const singleProduct = products.length === 1;

    return (
        <div className="tw-mt-2 tw-mb-1">
            <div
                ref={scrollRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                className={`tw-flex tw-gap-2 tw-overflow-x-auto tw-py-1 tw-select-none ${
                    singleProduct ? "tw-px-0" : "tw-px-0.5"
                }`}
                style={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    WebkitOverflowScrolling: "touch",
                    cursor: singleProduct ? "default" : isDragging ? "grabbing" : "grab",
                }}
            >
                {products.map((product) => {
                    const hasPromo = product.promoActive && product.formattedPromoPrice;
                    const displayPrice = formatPrice(product.formattedPrice || product.price);
                    const promoPrice = hasPromo ? formatPrice(product.formattedPromoPrice) : null;

                    return (
                        <div
                            key={product._id || product.id}
                            onClick={() => handleProductClick(product)}
                            className={`tw-flex-none tw-rounded-2xl tw-overflow-hidden tw-border tw-transition-all tw-cursor-pointer tw-group ${
                                singleProduct ? "tw-w-44" : "tw-w-40"
                            } ${
                                darkMode
                                    ? "tw-bg-gray-900/60 tw-border-white/10 hover:tw-border-white/20"
                                    : "tw-bg-white tw-border-gray-200 hover:tw-border-gray-300 hover:tw-shadow-md"
                            }`}
                        >
                            <ProductImage
                                src={product.images?.[0]}
                                alt={product.name}
                                darkMode={darkMode}
                            />
                            <div className="tw-p-3">
                                <div
                                    className={`tw-text-[13px] tw-font-semibold tw-leading-snug tw-line-clamp-2 tw-mb-2 ${
                                        darkMode
                                            ? "tw-text-gray-100"
                                            : "tw-text-gray-900"
                                    }`}
                                    style={{ minHeight: singleProduct ? undefined : "2.4rem" }}
                                >
                                    {product.name}
                                </div>
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <div className="tw-flex tw-items-baseline tw-gap-2 tw-flex-wrap">
                                        {hasPromo ? (
                                            <>
                                                <span
                                                    className={`tw-text-sm tw-font-bold ${
                                                        darkMode
                                                            ? "tw-text-green-400"
                                                            : "tw-text-green-600"
                                                    }`}
                                                >
                                                    {promoPrice}
                                                </span>
                                                <span
                                                    className={`tw-text-[11px] tw-line-through ${
                                                        darkMode
                                                            ? "tw-text-gray-500"
                                                            : "tw-text-gray-400"
                                                    }`}
                                                >
                                                    {displayPrice}
                                                </span>
                                            </>
                                        ) : (
                                            <span
                                                className={`tw-text-sm tw-font-bold ${
                                                    darkMode
                                                        ? "tw-text-gray-100"
                                                        : "tw-text-gray-900"
                                                }`}
                                            >
                                                {displayPrice}
                                            </span>
                                        )}
                                    </div>
                                    {onMentionProduct && (
                                        <button
                                            type="button"
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                onMentionProduct(product);
                                            }}
                                            className={`tw-shrink-0 tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors tw-border-0 ${
                                                darkMode
                                                    ? "tw-bg-white/10 tw-text-gray-400 hover:tw-bg-white/20 hover:tw-text-gray-200"
                                                    : "tw-bg-gray-100 tw-text-gray-500 hover:tw-bg-primary/10 hover:tw-text-primary"
                                            }`}
                                            title="Mention this product"
                                        >
                                            <IoChatbubbleEllipsesOutline size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
